import * as React from 'react';
import styled from 'styled-components';
import MDSpinner from 'react-md-spinner';

import { ArrowIcon } from './Icons';
import { InputError } from './InputError';
import { useClickoutside } from '../../../../utils/hooks/useClickOutside';

interface ISelect {
  nybygg?: boolean;
  name: string;
  label?: string;
  labelAfter?: string;
  placeholder?: string;
  disabled?: boolean;
  items: Array<any>;
  selected?: string | number;
  error?: boolean;
  errorMessage?: string;
  onSelect: (item: any) => void;
  loading?: boolean;
  children?: any;
}

export const Select: React.FC<ISelect> = React.memo((props) => {
  const selectRef = React.useRef();
  const [open, setOpen] = React.useState(false);

  useClickoutside({
    ref: selectRef,
    onClickOutside: () => {
      setOpen(false);
    },
  });

  return (
    <StyledInputWrapper ref={selectRef}>
      {props.label ? (
        <label>
          {props.label}{' '}
          {props.labelAfter ? <span>{props.labelAfter}</span> : null}
        </label>
      ) : null}
      {props.errorMessage ? (
        <InputError>{props.errorMessage}</InputError>
      ) : null}
      <StyledSelectWrapper>
        <StyledSelect
          className={props.nybygg ? 'nybygg' : ''}
          disabled={props.disabled || props.loading}
          onClick={!props.disabled ? () => setOpen(!open) : null}
          id={`select-${props.name}`}
          placeholder={props.placeholder}
          value={props.selected}
          error={props.error}
        >
          {props.selected ? props.selected : props.placeholder}
          {!props.loading ? (
            <ArrowIcon
              className={
                open ? 'select-arrow-icon select-open' : 'select-arrow-icon'
              }
            />
          ) : null}
          {props.loading ? (
            <MDSpinner className="select-loader" size={20} singleColor="#000" />
          ) : null}
        </StyledSelect>
        {open ? (
          <StyledSelectList className={!props.label ? 'no-label' : ''}>
            {props?.items?.map((item: any, index: number) => {
              return (
                <StyledSelectListItem
                  onClick={() => {
                    setOpen(false);
                    if (typeof props.onSelect === 'function') {
                      props.onSelect(item);
                    }
                  }}
                  key={`select_option_${props.name}_${index}`}
                >
                  {item.label}
                </StyledSelectListItem>
              );
            })}
          </StyledSelectList>
        ) : null}
      </StyledSelectWrapper>
    </StyledInputWrapper>
  );
});

const StyledInputWrapper = styled.div<any>`
  width: 100%;
  position: relative;
  user-select: none;

  label {
    display: block;
    color: ${(props) => props.theme.input.label};
    margin-bottom: 10px;

    span {
      color: ${(props) => props.theme.input.labelAfter};
    }
  }
  .arrowdown {
    position: absolute;
    right: 10px;
    height: 24px;
    svg {
      width: 20px;
    }
    path {
      stroke: #fff;
    }
  }
`;

const StyledSelectWrapper = styled.div`
  position: relative;
`;

const StyledSelect: any = styled.div<any>`
  position: relative;
  display: block;
  padding: 15px 40px 15px 15px;
  width: 100%;
  background: ${({ theme }) => theme.colors.primary};
  font-weight: 400;
  color: #000;
  border: ${(props: any) =>
    props.error
      ? `solid 2px ${props.theme.input.error}`
      : 'solid 2px transparent'};
  outline: none;
  border-radius: 0;
  ${({ disabled }) => (disabled ? `pointer-events: none;` : '')}
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    cursor: pointer;
  }

  .select-arrow-icon {
    position: absolute;
    top: 0;
    right: 10px;
    bottom: 0;
    margin: auto;
    width: 20px;
    height: 20px;
    svg {
      transition: transform 0.15s ease-in-out;
      transform: rotateX(0deg);
      width: 20px;
      height: 20px;
      path {
        stroke-width: 3px;
        stroke: #000;
      }
    }
    &.select-open {
      svg {
        transform: rotateX(180deg);
      }
    }
  }

  &.nybygg {
    background: rgb(255, 255, 255);
    color: rgb(0, 0, 0);
    .select-arrow-icon {
      svg {
        path {
          stroke: rgb(0, 0, 0);
        }
      }
    }

    @media all and (max-width: 500px) {
      width: 100%;
    }
  }

  .select-loader {
    position: absolute !important;
    right: 10px;
    top: 0;
    bottom: 0;
    margin: auto;
  }
`;

const StyledSelectList = styled.ul`
  width: 100%;
  position: absolute;
  display: block;
  z-index: 100;
  top: 52px;
  user-select: none;
  padding: 0;
  margin: 0;
  max-height: 120px;
  overflow-x: hidden;
  overflow-y: scroll;

  &.no-label {
    top: 52px;
  }
`;

const StyledSelectListItem = styled.li`
  display: block;
  padding: 10px 15px;
  background: rgb(40, 40, 40);
  color: white;

  &:nth-of-type(even) {
    background: rgb(50, 50, 50);
  }

  &:hover {
    cursor: pointer;
  }
`;
